import axios from "axios";
import { authClientWeb } from "react-redux-django-auth/web";

export type SocialPlatform = "facebook" | "instagram" | "twitter" | "linkedin" | "threads" | "tiktok";
export type SocialPostStatus = "pending" | "published" | "failed" | "skipped";

export interface SocialPostLog {
  id: number;
  broadcast: number;
  platform: SocialPlatform | string;
  status: SocialPostStatus;
  external_post_id?: string | null;
  external_post_url?: string | null;
  error_message?: string | null;
  response_data?: Record<string, any>;
  created_at: string;
}

export interface SocialBroadcast {
  id: number;
  title?: string;
  message: string;
  link_url?: string;
  image_url?: string;
  target_platforms: (SocialPlatform | string)[];
  content_type?: number | null;
  object_id?: number | null;
  created_at: string;
  updated_at: string;
  logs: SocialPostLog[];
}

export interface SocialBroadcastPayload {
  message: string;
  title?: string;
  link_url?: string;
  image_url?: string;
  platforms?: (SocialPlatform | string)[];
}

export interface SocialPlatformConfig {
  id: number;
  platform: SocialPlatform;
  is_enabled: boolean;
  page_or_account_id?: string;
  has_access_token?: boolean;
  has_access_token_secret?: boolean;
  has_api_key?: boolean;
  has_api_secret?: boolean;
  has_bearer_token?: boolean;
  extra_data?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface SocialPlatformConfigPayload {
  platform: SocialPlatform | string;
  is_enabled?: boolean;
  access_token?: string;
  access_token_secret?: string;
  page_or_account_id?: string;
  api_key?: string;
  api_secret?: string;
  bearer_token?: string;
  extra_data?: Record<string, any>;
}

const getClient = () => {
  try {
    const client = authClientWeb();
    if (client) return client;
  } catch {
    // fallback if authClientWeb is not yet initialized
  }
  const token = localStorage.getItem("access");
  const baseURL = process.env.REACT_APP_API_URL || "";
  return axios.create({
    baseURL,
    headers: {
      ...(token ? { Authorization: `JWT ${token}` } : {}),
    },
  });
};

export const socialBroadcastService = {
  // GET: Fetch list of social broadcasts
  async getBroadcasts(signal?: AbortSignal): Promise<SocialBroadcast[]> {
    const client = getClient();
    const response = await client.get<SocialBroadcast[]>("/social/broadcast/", { signal });
    return response.data;
  },

  // GET: Fetch single broadcast detail with per-platform logs
  async getBroadcastDetail(id: number | string, signal?: AbortSignal): Promise<SocialBroadcast> {
    const client = getClient();
    const response = await client.get<SocialBroadcast>(`/social/broadcast/${id}/`, { signal });
    return response.data;
  },

  // POST: Send broadcast across selected/enabled platforms
  async sendBroadcast(payload: SocialBroadcastPayload): Promise<SocialBroadcast> {
    const client = getClient();
    const response = await client.post<SocialBroadcast>("/social/broadcast/", payload);
    return response.data;
  },

  // DELETE: Delete broadcast record
  async deleteBroadcast(id: number): Promise<void> {
    const client = getClient();
    await client.delete(`/social/broadcast/${id}/`);
  },

  // GET: List platform configurations
  async getConfigs(signal?: AbortSignal): Promise<SocialPlatformConfig[]> {
    const client = getClient();
    const response = await client.get<SocialPlatformConfig[]>("/social/configs/", { signal });
    return response.data;
  },

  // POST: Create platform configuration override
  async createConfig(payload: SocialPlatformConfigPayload): Promise<SocialPlatformConfig> {
    const client = getClient();
    const response = await client.post<SocialPlatformConfig>("/social/configs/", payload);
    return response.data;
  },

  // PUT: Update platform configuration override
  async updateConfig(
    id: number,
    payload: Partial<SocialPlatformConfigPayload>
  ): Promise<SocialPlatformConfig> {
    const client = getClient();
    const response = await client.put<SocialPlatformConfig>(`/social/configs/${id}/`, payload);
    return response.data;
  },

  // DELETE: Delete platform configuration override
  async deleteConfig(id: number): Promise<void> {
    const client = getClient();
    await client.delete(`/social/configs/${id}/`);
  },

  // GET: List execution audit logs
  async getLogs(
    params?: { platform?: string; status?: string; broadcast_id?: number | string },
    signal?: AbortSignal
  ): Promise<SocialPostLog[]> {
    const client = getClient();
    const queryParams: Record<string, string> = {};
    if (params?.platform && params.platform !== "all") queryParams.platform = params.platform;
    if (params?.status && params.status !== "all") queryParams.status = params.status;
    if (params?.broadcast_id) queryParams.broadcast_id = String(params.broadcast_id);

    const response = await client.get<SocialPostLog[]>("/social/logs/", {
      params: queryParams,
      signal,
    });
    return response.data;
  },
};
